import { DocumentNode, InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import {
  ClaimError,
  PaymentMethod,
  TransactionErrorCodes,
  TransactionErrorDisplay,
} from '@bybeam/platform-types';
import { PaymentRail } from '@platform-ui-common/utilities/paymentMethods.js';
import SubmitCheckClaimMutation from '@platform-ui/claim/graphql/SubmitCheckClaimMutation.graphql';
import SubmitDirectDepositClaimMutation from '@platform-ui/claim/graphql/SubmitDirectDepositClaimMutation.graphql';
import SubmitPhysicalCardClaimMutation from '@platform-ui/claim/graphql/SubmitPhysicalCardClaimMutation.graphql';
import SubmitVirtualCardClaimMutation from '@platform-ui/claim/graphql/SubmitVirtualCardClaimMutation.graphql';
import SubmitZelleClaimMutation from '@platform-ui/claim/graphql/SubmitZelleClaimMutation.graphql';
import { ClaimPayload } from '@platform-ui/claim/hooks/useClaim.js';
import IssueFunds from '@platform-ui/graphql/mutations/IssueFundsMutation.graphql';
import { useCallback, useMemo } from 'react';

enum MutationNames {
  DirectDeposit = 'claimDirectDeposit',
  Check = 'claimCheck',
  Zelle = 'claimZelle',
  PhysicalCard = 'claimPhysicalCard',
  VirtualCard = 'claimVirtualCard',
}

const mutationDetails: Record<PaymentRail, { mutation: DocumentNode; name: MutationNames }> = {
  [PaymentMethod.Check]: {
    mutation: SubmitCheckClaimMutation,
    name: MutationNames.Check,
  },
  [PaymentMethod.DirectDeposit]: {
    mutation: SubmitDirectDepositClaimMutation,
    name: MutationNames.DirectDeposit,
  },
  [PaymentMethod.PhysicalCard]: {
    mutation: SubmitPhysicalCardClaimMutation,
    name: MutationNames.PhysicalCard,
  },
  [PaymentMethod.VirtualCard]: {
    mutation: SubmitVirtualCardClaimMutation,
    name: MutationNames.VirtualCard,
  },
  [PaymentMethod.Zelle]: {
    mutation: SubmitZelleClaimMutation,
    name: MutationNames.Zelle,
  },
};

export interface ClaimMutationResponse {
  fulfillment: {
    [key in MutationNames | 'issueFunds']: { metadata: { status: number; errors: string[] } };
  };
}

export interface UseSubmitClaimProps {
  fulfillmentId: string;
  method: PaymentRail;
  payload?: ClaimPayload;
  refetchQueries?: InternalRefetchQueriesInclude;
  caseId?: string;
  admin?: boolean;
}

export default function useSubmitClaim({
  fulfillmentId,
  caseId,
  method,
  payload,
  refetchQueries,
  admin,
}: UseSubmitClaimProps): {
  doMutation: () => Promise<boolean>;
  loading: boolean;
  error: ClaimError;
} {
  const { mutation, name } = admin
    ? ({ mutation: IssueFunds, name: 'issueFunds' } as {
        mutation: DocumentNode;
        name: 'issueFunds';
      })
    : mutationDetails[method];
  const [submitClaim, { data, loading, error: transportError }] =
    useMutation<ClaimMutationResponse>(mutation, {
      ...(refetchQueries && { refetchQueries }),
    });

  const doMutation = useCallback(async () => {
    try {
      const {
        data: {
          fulfillment: {
            [name]: { metadata },
          },
        },
      } = await submitClaim({
        variables: {
          input: {
            ...(payload && { ...payload }),
            id: fulfillmentId,
          },
          ...(caseId && { caseId }),
        },
      });

      if (metadata.status === 200) return true;
      return false;
    } catch (e) {
      return false;
    }
  }, [caseId, fulfillmentId, name, payload, submitClaim]);

  let error: ClaimError;
  const errorCodes = Object.values(TransactionErrorCodes) as string[];
  // TODO separate network errors from validation errors - we can likely retry on
  // validation errors or at least give the user a more useful message. Another
  // improvment we can add here is to change the structure of the errors returned
  // from the API so we can use the payments service provided retryable value instead
  // of comparing to the list of retryable error codes.
  if (transportError) {
    error = {
      message: 'Something went wrong!',
      retryable: false,
    };
  } else if (data?.fulfillment?.[name].metadata.errors) {
    const [code, message] = data.fulfillment[name].metadata.errors;
    error = {
      message: TransactionErrorDisplay[code] || message,
      retryable: !!(data?.fulfillment?.[name].metadata.status === 400 && errorCodes.includes(code)),
    };
  }

  const memo = useMemo(() => ({ doMutation, loading, error }), [doMutation, loading, error]);
  return memo;
}
